import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Box,
  Stepper,
  Step,
  StepLabel,
  Button,
  CircularProgress,
  Alert,
  useMediaQuery,
  useTheme
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

import { useVenue } from '../../contexts/VenueContext';
import { Package } from '../../types/Venue';
import { getPackageById, updatePackage } from '../../services/venueService';

// Import form components
import PackageBasicInfoForm from './components/PackageBasicInfoForm';
import PackagePricingForm from './components/PackagePricingForm';
import PackageIncludedItemsForm from './components/PackageIncludedItemsForm';
import PackageAvailabilityForm from './components/PackageAvailabilityForm';
import PackageImagesForm from './components/PackageImagesForm';

const steps = [
  'Basic Info',
  'Pricing',
  'Included Items',
  'Availability',
  'Images'
];

const EditPackage: React.FC = () => {
  const { venueId, packageId } = useParams<{ venueId: string; packageId: string }>();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [activeStep, setActiveStep] = useState(0);
  const [packageData, setPackageData] = useState<Package | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Fetch the package data
  useEffect(() => {
    const fetchPackage = async () => {
      if (!venueId || !packageId) return;
      
      try {
        setLoading(true);
        const data = await getPackageById(venueId, packageId);
        if (data) {
          setPackageData(data);
        } else {
          setError('Package not found');
        }
      } catch (err) {
        console.error('Error fetching package:', err);
        setError('Failed to load package data');
      } finally {
        setLoading(false);
      }
    };

    fetchPackage();
  }, [venueId, packageId]);

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleFormSubmit = async () => {
    if (!venueId || !packageId || !packageData) return;
    
    setSubmitting(true);
    setError(null);
    
    try {
      await updatePackage(venueId, packageId, packageData);
      setSuccess(true);
      // Redirect after a short delay to show success message
      setTimeout(() => {
        navigate(`/venues/${venueId}/packages/${packageId}`);
      }, 1500);
    } catch (err) {
      console.error('Error updating package:', err);
      setError('Failed to update package. Please try again.');
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    const confirmCancel = window.confirm('Are you sure you want to cancel? Any unsaved changes will be lost.');
    if (confirmCancel) {
      navigate(`/venues/${venueId}/packages/${packageId}`);
    }
  };

  const renderStepContent = (step: number) => {
    if (!packageData) return null;

    switch (step) {
      case 0:
        return (
          <PackageBasicInfoForm 
            packageData={packageData} 
            onChange={(updatedData: Partial<Package>) => setPackageData({...packageData, ...updatedData})}
          />
        );
      case 1:
        return (
          <PackagePricingForm 
            packageData={packageData} 
            onChange={(updatedData: Partial<Package>) => setPackageData({...packageData, ...updatedData})}
          />
        );
      case 2:
        return (
          <PackageIncludedItemsForm 
            packageData={packageData} 
            onChange={(updatedData: Partial<Package>) => setPackageData({...packageData, ...updatedData})}
          />
        );
      case 3:
        return (
          <PackageAvailabilityForm 
            packageData={packageData} 
            onChange={(updatedData: Partial<Package>) => setPackageData({...packageData, ...updatedData})}
          />
        );
      case 4:
        return (
          <PackageImagesForm 
            packageData={packageData} 
            onChange={(updatedData: Partial<Package>) => setPackageData({...packageData, ...updatedData})}
            venueId={venueId || ''}
          />
        );
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && !packageData) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="error">{error}</Alert>
        <Button 
          startIcon={<ArrowBackIcon />} 
          onClick={() => navigate(`/venues/${venueId}`)}
          sx={{ mt: 2 }}
        >
          Back to Venue
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Paper elevation={3} sx={{ p: isMobile ? 2 : 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Button 
            startIcon={<ArrowBackIcon />} 
            onClick={() => navigate(`/venues/${venueId}/packages/${packageId}`)} 
            sx={{ mr: 2 }}
          >
            Back
          </Button>
          <Typography variant="h5" component="h1">
            Edit Package
          </Typography>
          <Button 
            variant="outlined"
            color="error"
            onClick={handleCancel}
          >
            Cancel Edit
          </Button>
        </Box>

        {success && (
          <Alert severity="success" sx={{ mb: 3 }}>
            Package updated successfully!
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <Stepper 
          activeStep={activeStep} 
          sx={{ mb: 4 }}
          alternativeLabel={isMobile}
          orientation={isMobile ? "horizontal" : "horizontal"}
        >
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{isMobile ? "" : label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {renderStepContent(activeStep)}

        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
          <Button
            disabled={activeStep === 0 || submitting}
            onClick={handleBack}
          >
            Back
          </Button>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              color="error"
              onClick={handleCancel}
              disabled={submitting}
            >
              Cancel
            </Button>
            {activeStep === steps.length - 1 ? (
              <Button 
                variant="contained" 
                onClick={handleFormSubmit}
                disabled={submitting}
              >
                {submitting ? <CircularProgress size={24} /> : 'Update Package'}
              </Button>
            ) : (
              <Button
                variant="contained"
                onClick={handleNext}
              >
                Next
              </Button>
            )}
          </Box>
        </Box>
      </Paper>
    </Container>
  );
};

export default EditPackage;
